/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { createDkvCustomer } from '../fn/pdi-dkv-api/create-dkv-customer';
import { CreateDkvCustomer$Params } from '../fn/pdi-dkv-api/create-dkv-customer';
import { createDkvLimits } from '../fn/pdi-dkv-api/create-dkv-limits';
import { CreateDkvLimits$Params } from '../fn/pdi-dkv-api/create-dkv-limits';
import { CustomerLimits } from '../models/customer-limits';
import { CustomerStatus } from '../models/customer-status';
import { CustomerSubmission } from '../models/customer-submission';
import { CustomerTerminationResponseBody } from '../models/customer-termination-response-body';
import { CustomerUpdateSubmission } from '../models/customer-update-submission';
import { getCustomerSubmissionStatus } from '../fn/pdi-dkv-api/get-customer-submission-status';
import { GetCustomerSubmissionStatus$Params } from '../fn/pdi-dkv-api/get-customer-submission-status';
import { getCustomerSubmissionStatusLongPoll } from '../fn/pdi-dkv-api/get-customer-submission-status-long-poll';
import { GetCustomerSubmissionStatusLongPoll$Params } from '../fn/pdi-dkv-api/get-customer-submission-status-long-poll';
import { getDkvLimits } from '../fn/pdi-dkv-api/get-dkv-limits';
import { GetDkvLimits$Params } from '../fn/pdi-dkv-api/get-dkv-limits';
import { lockDkvCustomer } from '../fn/pdi-dkv-api/lock-dkv-customer';
import { LockDkvCustomer$Params } from '../fn/pdi-dkv-api/lock-dkv-customer';
import { StatusPollingDto } from '../models/status-polling-dto';
import { terminateDkvCustomer } from '../fn/pdi-dkv-api/terminate-dkv-customer';
import { TerminateDkvCustomer$Params } from '../fn/pdi-dkv-api/terminate-dkv-customer';
import { updateDkvCustomer } from '../fn/pdi-dkv-api/update-dkv-customer';
import { UpdateDkvCustomer$Params } from '../fn/pdi-dkv-api/update-dkv-customer';

@Injectable({ providedIn: 'root' })
export class PdiDkvApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `createDkvCustomer()` */
  static readonly CreateDkvCustomerPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer-submission';

  /**
   * Create a new customer submission via DKV PDI API.
   *
   * Create a new customer submission via DKV PDI API for DKV Cockpit (customer is created on dkv site / async process, takes up to minutes)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDkvCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvCustomer$Response(params: CreateDkvCustomer$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerSubmission>> {
    return createDkvCustomer(this.http, this.rootUrl, params, context);
  }

  /**
   * Create a new customer submission via DKV PDI API.
   *
   * Create a new customer submission via DKV PDI API for DKV Cockpit (customer is created on dkv site / async process, takes up to minutes)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDkvCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvCustomer(params: CreateDkvCustomer$Params, context?: HttpContext): Observable<CustomerSubmission> {
    return this.createDkvCustomer$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerSubmission>): CustomerSubmission => r.body)
    );
  }

  /** Path part for operation `updateDkvCustomer()` */
  static readonly UpdateDkvCustomerPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer-submission/{dkvCustomerNumber}';

  /**
   * Create Update submission to pdi api for an existing customer.
   *
   * Create Update submission to pdi api for an existing customer for DKV Cockpit (customer is updated on dkv site / async process, takes up to minutes)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDkvCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDkvCustomer$Response(params: UpdateDkvCustomer$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerUpdateSubmission>> {
    return updateDkvCustomer(this.http, this.rootUrl, params, context);
  }

  /**
   * Create Update submission to pdi api for an existing customer.
   *
   * Create Update submission to pdi api for an existing customer for DKV Cockpit (customer is updated on dkv site / async process, takes up to minutes)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDkvCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDkvCustomer(params: UpdateDkvCustomer$Params, context?: HttpContext): Observable<CustomerUpdateSubmission> {
    return this.updateDkvCustomer$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerUpdateSubmission>): CustomerUpdateSubmission => r.body)
    );
  }

  /** Path part for operation `getCustomerSubmissionStatus()` */
  static readonly GetCustomerSubmissionStatusPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer-submission/{submissionId}';

  /**
   * Get Status of DKV Customer via pdi api, after create or update pdi call, this must be polled until customer is completed.
   *
   * Get Submission Status. Unless there is an error, it answers either with completed or in progress status. This request might take some time.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerSubmissionStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerSubmissionStatus$Response(params: GetCustomerSubmissionStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<StatusPollingDto>> {
    return getCustomerSubmissionStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * Get Status of DKV Customer via pdi api, after create or update pdi call, this must be polled until customer is completed.
   *
   * Get Submission Status. Unless there is an error, it answers either with completed or in progress status. This request might take some time.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCustomerSubmissionStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerSubmissionStatus(params: GetCustomerSubmissionStatus$Params, context?: HttpContext): Observable<StatusPollingDto> {
    return this.getCustomerSubmissionStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<StatusPollingDto>): StatusPollingDto => r.body)
    );
  }

  /** Path part for operation `getCustomerSubmissionStatusLongPoll()` */
  static readonly GetCustomerSubmissionStatusLongPollPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer-submission/{submissionId}/long-polling';

  /**
   * Get Status of DKV Customer via pdi api, after create or update pdi call, this must be polled until customer is completed.
   *
   * Long polling of dkv pdi api, unless there is an error, it answers either with completed or in progress status. This request might take some time, because of the long polling against dkv api.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerSubmissionStatusLongPoll()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerSubmissionStatusLongPoll$Response(params: GetCustomerSubmissionStatusLongPoll$Params, context?: HttpContext): Observable<StrictHttpResponse<StatusPollingDto>> {
    return getCustomerSubmissionStatusLongPoll(this.http, this.rootUrl, params, context);
  }

  /**
   * Get Status of DKV Customer via pdi api, after create or update pdi call, this must be polled until customer is completed.
   *
   * Long polling of dkv pdi api, unless there is an error, it answers either with completed or in progress status. This request might take some time, because of the long polling against dkv api.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCustomerSubmissionStatusLongPoll$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerSubmissionStatusLongPoll(params: GetCustomerSubmissionStatusLongPoll$Params, context?: HttpContext): Observable<StatusPollingDto> {
    return this.getCustomerSubmissionStatusLongPoll$Response(params, context).pipe(
      map((r: StrictHttpResponse<StatusPollingDto>): StatusPollingDto => r.body)
    );
  }

  /** Path part for operation `terminateDkvCustomer()` */
  static readonly TerminateDkvCustomerPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer/{dkvCustomerNumber}';

  /**
   * Removes a customer via DKV PDI API.
   *
   * Removes a customer via DKV PDI API for DKV Cockpit (customer is ended on dkv site / async process, takes up to minutes)
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `terminateDkvCustomer()` instead.
   *
   * This method doesn't expect any request body.
   */
  terminateDkvCustomer$Response(params: TerminateDkvCustomer$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerTerminationResponseBody>> {
    return terminateDkvCustomer(this.http, this.rootUrl, params, context);
  }

  /**
   * Removes a customer via DKV PDI API.
   *
   * Removes a customer via DKV PDI API for DKV Cockpit (customer is ended on dkv site / async process, takes up to minutes)
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `terminateDkvCustomer$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  terminateDkvCustomer(params: TerminateDkvCustomer$Params, context?: HttpContext): Observable<CustomerTerminationResponseBody> {
    return this.terminateDkvCustomer$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerTerminationResponseBody>): CustomerTerminationResponseBody => r.body)
    );
  }

  /** Path part for operation `lockDkvCustomer()` */
  static readonly LockDkvCustomerPath = '/svgs/{svgId}/customers/{customerId}/dkv-customer/{dkvCustomerNumber}';

  /**
   * Locks a customer via DKV PDI API.
   *
   * Locks a customer via DKV PDI API for DKV Cockpit, customer is locked on dkv site
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `lockDkvCustomer()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  lockDkvCustomer$Response(params: LockDkvCustomer$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerStatus>> {
    return lockDkvCustomer(this.http, this.rootUrl, params, context);
  }

  /**
   * Locks a customer via DKV PDI API.
   *
   * Locks a customer via DKV PDI API for DKV Cockpit, customer is locked on dkv site
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `lockDkvCustomer$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  lockDkvCustomer(params: LockDkvCustomer$Params, context?: HttpContext): Observable<CustomerStatus> {
    return this.lockDkvCustomer$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerStatus>): CustomerStatus => r.body)
    );
  }

  /** Path part for operation `getDkvLimits()` */
  static readonly GetDkvLimitsPath = '/svgs/{svgId}/customers/{customerId}/limits-config/{dkvCustomerNumber}';

  /**
   * Retrieve customer limits configuration from DKV PDI API.
   *
   * Retrieve customer limits configuration from dkv api, those limits restrict the customers fuel cards usage
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDkvLimits()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDkvLimits$Response(params: GetDkvLimits$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerLimits>> {
    return getDkvLimits(this.http, this.rootUrl, params, context);
  }

  /**
   * Retrieve customer limits configuration from DKV PDI API.
   *
   * Retrieve customer limits configuration from dkv api, those limits restrict the customers fuel cards usage
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDkvLimits$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDkvLimits(params: GetDkvLimits$Params, context?: HttpContext): Observable<CustomerLimits> {
    return this.getDkvLimits$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerLimits>): CustomerLimits => r.body)
    );
  }

  /** Path part for operation `createDkvLimits()` */
  static readonly CreateDkvLimitsPath = '/svgs/{svgId}/customers/{customerId}/limits-config/{dkvCustomerNumber}';

  /**
   * Create limits configuration for a dkv customer via DKV PDI API.
   *
   * Create limits configuration for a dkv customer via DKV PDI API, to restrict the usage of this customers fuel cards
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDkvLimits()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvLimits$Response(params: CreateDkvLimits$Params, context?: HttpContext): Observable<StrictHttpResponse<CustomerLimits>> {
    return createDkvLimits(this.http, this.rootUrl, params, context);
  }

  /**
   * Create limits configuration for a dkv customer via DKV PDI API.
   *
   * Create limits configuration for a dkv customer via DKV PDI API, to restrict the usage of this customers fuel cards
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDkvLimits$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDkvLimits(params: CreateDkvLimits$Params, context?: HttpContext): Observable<CustomerLimits> {
    return this.createDkvLimits$Response(params, context).pipe(
      map((r: StrictHttpResponse<CustomerLimits>): CustomerLimits => r.body)
    );
  }

}
